import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@shared/shared.module';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  Validator,
  ReactiveFormsModule,
  UntypedFormControl
} from '@angular/forms';
import { BundledFeatureCategory, OnboardingCountryInterface, OnboardingDocusign, SignupPlan, SignupSubscriptionData } from '@shared/interfaces/onboarding-saga.interface';
import { Subscription } from 'rxjs';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { ActivatedRoute } from '@angular/router';
import { createNullableState, createState } from '@shared/helpers/create-state.helper';

@Component({
  selector: 'app-signup-subscription',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  providers: [
    // Register as NG_VALUE_ACCESSOR so Angular knows this is a form control
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SignupSubscriptionComponent),
      multi: true
    },
    // Register as NG_VALIDATORS so Angular can validate this control
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SignupSubscriptionComponent),
      multi: true
    },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    }
  ],
  templateUrl: './signup-subscription.component.html',
  styleUrls: ['./signup-subscription.component.scss']
})
export class SignupSubscriptionComponent implements ControlValueAccessor, Validator, OnDestroy, OnChanges, OnInit {
  @Input() countries: OnboardingCountryInterface[] = [];
  @Input() plan: SignupPlan;

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);

  private readonly subscription: Subscription;
  public form: FormGroup;
  public sportOptions: Array<{ value: string; name: string }>;
  public showLactate = true;
  public showPPD = true;

  // Component state
  guid = createNullableState<string>();
  docusignEvent = createNullableState<string>();
  docusignConfirmed = createState<boolean>(false);


  // These callbacks are registered by Angular
  private onChange: (value: SignupSubscriptionData | null) => void = () => { };
  private onTouched: () => void = () => { };

  constructor() {
    this.form = this.fb.group({
      billing_country: ['', Validators.required],
      vat_number: [''],
      organization_name: ['', Validators.required],
      vat_required: [false],
      billing_period: [1, Validators.required],
      use_lactate: [{ value: true, disabled: false }],
      use_ppd: [{ value: true, disabled: false }],
      sports: new UntypedFormControl(''),
    });

    // Subscribe to internal form changes and propagate to parent
    this.subscription = this.form.valueChanges.subscribe(value => {
      this.onChange(this.form.valid ? value : null);
    });

    // Mark as touched when any field is touched
    this.subscription.add(
      this.form.statusChanges.subscribe(() => {
        if (this.form.touched) {
          this.onTouched();
        }
      })
    );
  }

  ngOnInit(): void {
    const routeSnapshot = this.route.snapshot;
    this.guid.set(routeSnapshot?.params?.guid);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.plan) {
      this.sportOptions = (this.plan?.bundled_features ?? [])
        .filter(f => f.category === BundledFeatureCategory.SPORTS)
        .map(f => ({ value: f.slug, name: f.name }));
      this.form.patchValue({
        sports: this.sportOptions
      });
      this.showLactate = this.plan?.bundled_features?.find(f => f.slug === "lactate") != null;
      this.showPPD = this.plan.bundled_features?.find(f => f.slug === 'ppd') != null;

      this.form.patchValue({
        use_lactate: this.showLactate,
        use_ppd: this.showPPD
      });
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public testTypeDisabled(testType: string): boolean {
    // TODO: Use plan features to determine this
    return false;
  }

  public sportFeatures() {
    if (!this.plan.bundled_features) {
      return null
    }

    const sportFeatures = this.plan.bundled_features.filter(f => f.category === BundledFeatureCategory.SPORTS)
    if (sportFeatures && Array.isArray(sportFeatures) && sportFeatures.length > 0) {
      return sportFeatures
    }

    return null
  }

  public onSelectSport(value: any): void {
    this.form.get('sports')?.setValue(value);
  }

  // Called when Angular wants to write a value to this component
  // Example: parent does formGroup.patchValue({ registration: {...} })
  writeValue(value: SignupSubscriptionData | null): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    } else {
      this.form.reset({}, { emitEvent: false });
    }
  }

  // Angular calls this to register the callback that should be called
  // when the component's value changes
  registerOnChange(fn: (value: SignupSubscriptionData | null) => void): void {
    this.onChange = fn;
  }

  // Angular calls this to register the callback that should be called
  // when the component is "touched" (blurred)
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Angular calls this when the control's disabled state should change
  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  // Called by Angular to validate this control
  // Return null if valid, or an error object if invalid
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form.valid) {
      return null;
    }

    // Return errors from internal form
    return {
      subscriptionForm: {
        valid: false,
        errors: this.getFormErrors()
      }
    };
  }

  // Helper to collect all errors from internal form
  private getFormErrors(): any {
    const errors: any = {};
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    if (this.form.errors) {
      errors._form = this.form.errors;
    }
    return errors;
  }
}
